/* ------------------------------------------------------------------ */
/*  Appointment date/time preview formatter (PRD.221)                 */
/*                                                                    */
/*  This does NOT build WhatsApp template parameters. Since PRD.254    */
/*  the backend derives every {{n}} from notification_templates        */
/*  .metadata, and the variable list differs per org — koncept-skin's  */
/*  reminder takes 4, gurusaispecialists' takes 5 and carries no date  */
/*  variable at all. The client sends only an appointment id.          */
/*                                                                    */
/*  What is left here is a human-readable summary of the appointment   */
/*  itself, shown in the send-confirmation dialog and used to gate the */
/*  action when date/time are missing.                                 */
/* ------------------------------------------------------------------ */

/**
 * Clinic/org timezone the appointment wall-clock values are assumed to be
 * expressed in. There is no org-timezone setting wired up yet, so this is
 * the documented fallback rather than an applied conversion (see below).
 */
export const DEFAULT_CLINIC_TIMEZONE = 'Asia/Manila';

/** Locale used to render the preview date/time. */
export const REMINDER_LOCALE = 'en-PH';

/**
 * Format an appointment's date + start time for display as `[date, time]`.
 *
 * `date` is "YYYY-MM-DD" and `startTime` is "HH:mm" — both are already
 * wall-clock values in clinic-local time, so NO timezone conversion is
 * applied (converting a wall-clock value would shift it incorrectly). The
 * Date objects are constructed from local parts purely so `Intl` can render
 * the locale-specific format.
 *
 * Returns `null` if either input is missing/malformed so callers can disable
 * the action rather than show a half-formed appointment.
 */
export function formatAppointmentPreview(date: string, startTime: string): [string, string] | null {
  if (!date || !startTime) return null;

  const dateParts = date.split('-').map(Number);
  const timeParts = startTime.split(':').map(Number);
  if (dateParts.length < 3 || timeParts.length < 2) return null;

  const [y, m, d] = dateParts;
  const [hh, mm] = timeParts;
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;

  const dateObj = new Date(y, m - 1, d);
  const timeObj = new Date(y, m - 1, d, hh, mm);

  const datePart = new Intl.DateTimeFormat(REMINDER_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateObj);

  const timePart = new Intl.DateTimeFormat(REMINDER_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(timeObj);

  return [datePart, timePart];
}

/**
 * Prefix of the reminder's `notification_jobs.client_reference` (PRD.300).
 *
 * The backend now OWNS the full reference and dates it —
 * `appointment:<id>:reminder:<localYYYY-MM-DD>` — so the 06:00 batch, the
 * same-day trigger and the manual action all dedupe against each other. The
 * client can no longer construct it (it does not know the clinic timezone),
 * so it matches by prefix to answer "has a reminder gone out for this
 * appointment on any day?".
 */
export function appointmentReminderReferencePrefix(appointmentId: string): string {
  return `appointment:${appointmentId}:reminder:`;
}

/**
 * Legacy undated reference used before PRD.300. Still recognized when reading
 * existing rows so historical reminders keep showing as sent.
 */
export function legacyAppointmentReminderReference(appointmentId: string): string {
  return `appointment:${appointmentId}:whatsapp-reminder`;
}

/** Whether a stored client_reference belongs to this appointment's reminder. */
export function isAppointmentReminderReference(
  clientReference: string | null | undefined,
  appointmentId: string,
): boolean {
  if (!clientReference) return false;
  return (
    clientReference.startsWith(appointmentReminderReferencePrefix(appointmentId)) ||
    clientReference === legacyAppointmentReminderReference(appointmentId)
  );
}
